import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import WapiService from 'wanderu-service-wapi';
import { makeQueryString } from 'wanderu-utils';

import {
    actions as carrierActions,
} from '../../appstate';
import {
    FILTER_FIELDS,
    MIX_TRIP_CARRIER_CODE,
} from '../../constants';
// The base duck, not the fetchCarrier module: all this needs is that duck's flag
// actions, not its thunk.
import {
    actions as fetchCarrierActions,
} from '../fetchCarrier/base';
import {
    namespace,
    actions,
    reducer,
    selectors,
} from './base';
import { createCatalogCache } from './catalogCache';

const logger = Logger.getLogger('wanderu-ducks-carriers:fetchAllCarriers');

// The list route is anonymous (wanderu/wapi#544, #1185): no X-TOKEN on either
// call site below. wapi verifies any token it is sent, so a browser holding an
// expired one would 401 on every edge-cache miss and silently re-auth. The by-id
// route in fetchCarrier still requires a token.
async function getMultipleCarrierData(carrierIds) {
    const query = {
        fields: FILTER_FIELDS.join(','),
        carrier_id: carrierIds && carrierIds.join(','),
    };

    try {
        const response = await WapiService.getWithoutAuth(`carriers.json${makeQueryString(query)}`);

        return response.result;
    } catch (ex) {
        logger.info('getMultipleCarrierData', 'error while fetching updated carrier data', ex.message);
    }
}

// An empty list is a bad response, not an empty catalog. Server side, caching it
// would strand every render on this pod for the rest of the TTL; client side, it
// would report success with no carriers in the store.
function assertIsCatalog(carrierData) {
    if (!carrierData || !carrierData.length) {
        throw new Error('no carrier data found');
    }
}

function buildCarrierMap(carrierData) {
    // Plain loop, not reduce+spread: spreading the accumulator copies every
    // previous key on each iteration (O(n^2) over ~1,400 carriers), which the
    // prod profiler showed as the single largest CPU frame in the SSR.
    const allCarriers = {};
    for (const carrier of carrierData) {
        allCarriers[carrier.carrier_id] = carrier;
    }

    return allCarriers;
}

async function fetchCatalog() {
    const query = {
        fields: FILTER_FIELDS.join(','),
    };

    // Straight to wapi rather than through getMultipleCarrierData: that helper
    // logs the error and returns undefined, and this is the path that has to be
    // able to say why a page rendered without carriers.
    const response = await WapiService.getWithoutAuth(`carriers.json${makeQueryString(query)}`);
    const carrierData = response && response.result;

    assertIsCatalog(carrierData);

    return buildCarrierMap(carrierData);
}

// One catalog per process, shared by every server render. Only loadCatalog
// reads it; `fetch` below is the client path, where the store's own success flag
// already prevents a second fetch and a module cache would only let one page's
// data outlive its store.
const catalogCache = createCatalogCache({
    fetchCatalog,
    onFetchError: (ex, { servingStale }) => logger.warn('catalogCache', ex, {
        note: servingStale
            ? 'carrier catalog refresh failed; serving the stale copy'
            : 'carrier catalog unavailable; renders will ship no carriers',
    }),
});

function fetch() {
    return async (dispatch) => {
        dispatch(actions.startWork());

        let error;

        try {
            const carrierData = await getMultipleCarrierData();
            assertIsCatalog(carrierData);

            dispatch(carrierActions.setAllCarrierData(buildCarrierMap(carrierData)));
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.warn('fetch', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });

            error = {
                message: ex.message,
            };
        } finally {
            dispatch(actions.endWork(undefined, undefined, error));
        }
    };
}

/**
 * The pod's carrier catalog. Server-only. Started before the search that decides
 * which carriers the page needs, so the wapi round trip overlaps it, and handed to
 * setReferencedCarriers afterwards — one load per render, whatever the cache does.
 *
 * Never rejects: a render without carrier names is better than no render, and the
 * cache reports the failure itself.
 *
 * @return {Promise<Object|undefined>} carriers keyed by code, or undefined
 */
async function loadCatalog() {
    try {
        return await catalogCache.load();
    } catch {
        // Reported by the cache's onFetchError, once per failed fetch rather than
        // once per render that finds it missing.
        return undefined;
    }
}

/**
 * Puts just the carriers a page renders into the store, instead of the whole
 * catalog: at ~1,400 carriers the full map is 646 KB of `__INITIAL_STATE__`
 * for the ~12 carriers a busy search results page actually shows.
 *
 * Deliberately leaves this duck's own success flag unset — the client refetches
 * the full catalog after mount, which later client-side searches need. Each
 * carrier that _is_ shipped is marked fetched so hydration doesn't re-request
 * the same data one carrier at a time.
 *
 * With no catalog it stores nothing: the client fetches the catalog after
 * mount, and SearchResult self-heals the carriers it still needs.
 *
 * @param {Array<String>} carrierCodes - the codes the page's results reference
 * @param {Object} [catalog] - the catalog for this render, from loadCatalog
 * @return {Function} thunk
 */
function setReferencedCarriers(carrierCodes, catalog) {
    return (dispatch) => {
        if (!catalog) {
            return;
        }

        const referenced = {};
        for (const carrierCode of [...carrierCodes || [], MIX_TRIP_CARRIER_CODE]) {
            const carrier = catalog[carrierCode];
            if (carrier) {
                referenced[carrierCode] = carrier;
            }
        }

        dispatch(carrierActions.setAllCarrierData(referenced));

        for (const carrierCode of Object.keys(referenced)) {
            // Three arguments on purpose: endWork treats a shorter call as
            // (meta, error) and would file the result under `default`.
            dispatch(fetchCarrierActions.endWork(carrierCode, undefined, undefined));
        }
    };
}

const fetchAllCarriersAction = {
    fetch,
    loadCatalog,
    setReferencedCarriers,
    ...selectors,
};

export {
    fetchAllCarriersAction,
};

export default {
    [namespace]: reducer,
};
